/**
 * LegalBasisBadge — E-93 Story 93.8.
 *
 * Renders a small colour-coded pill showing the GDPR legal basis that
 * will apply to the current template + an explanatory warning when the
 * basis is `MARKETING_CONSENT` (which materially changes the recipient
 * set — BOLTVEZETO needs to see this BEFORE hitting send, not after).
 *
 * Deliberately keeps the three variants visually distinct:
 * - TRANSACTIONAL → neutral grey (default, no call-to-attention)
 * - MARKETING_CONSENT → amber (opt-in gate will apply)
 * - LEGITIMATE_INTEREST → blue (informational — legal may review)
 * - unknown → red (template slug not in the mirror; contract drift)
 */

import type { TemplateLegalBasis } from '@/api/bulk-email';
import { useTranslation } from '@kgc/i18n';
import type { JSX } from 'react';

interface LegalBasisBadgeProps {
  legalBasis: TemplateLegalBasis | null;
}

export function LegalBasisBadge({ legalBasis }: LegalBasisBadgeProps): JSX.Element {
  const { t } = useTranslation();

  if (legalBasis === null) {
    return (
      <span
        className="inline-flex items-center rounded-full border border-destructive bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
        data-testid="legal-basis-unknown"
      >
        {t('email.bulkSend.legalBasis.unknown')}
      </span>
    );
  }

  const classes = classesForBasis(legalBasis);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
      data-testid={`legal-basis-${legalBasis.toLowerCase()}`}
    >
      {t(`email.bulkSend.legalBasis.${legalBasis}`)}
    </span>
  );
}

/**
 * Pure helper — exported so the logic test can pin the class mapping
 * without rendering the component (jsdom+Radix instability avoided,
 * matching the 95-9 precedent).
 */
export function classesForBasis(basis: TemplateLegalBasis): string {
  switch (basis) {
    case 'TRANSACTIONAL':
      return 'border border-muted-foreground/30 bg-muted text-muted-foreground';
    case 'MARKETING_CONSENT':
      return 'border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100';
    case 'LEGITIMATE_INTEREST':
      return 'border border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-100';
  }
}
